/**
 * Window size presets — number 단위 (react-rnd 가 number 를 요구).
 * vars.* CSS 토큰과는 별도 — config 레이어 상수.
 *
 * Authoritative for minWidth/minHeight floors:
 * - Commit `e64acb5be` (2026-03-27) "전체 윈도우 minWidth/minHeight 상향 조정":
 *   내부 테이블 minWidth(680–700px) + sidebar/padding → 1000×600 이상 floor 결정.
 *   `standard` preset 의 defaultWidth === minWidth 는 "default 가 곧 최소" 의도이지
 *   리사이즈 차단 목적이 아님.
 */

/** 피처 플래그 teaser 상태 윈도우 chrome. 모든 게이트 윈도우 공유. */
export const teaserWindowSize = {
  defaultWidth: 460,
  defaultHeight: 400,
  minWidth: 360,
  minHeight: 320,
} as const;

/** live 상태 윈도우 사이즈 프리셋. */
export const liveWindowSize = {
  /** today, work-log — text 위주, 좁아도 됨. 800 은 16px 본문 한 줄에 한글 50자쯤 들어가
   *  편한 줄 길이(35~45자)를 넘겼고, 빈 메모 에디터가 그 폭을 통째로 차지해 헛헛했다(2026-09-17 시안).
   *  기본값만 640 으로 내리고 하한 600 은 그대로 — 넓게 쓰는 사람의 리사이즈 자유는 두고
   *  기존 저장 위치는 프리셋 서명(resolvePersistedBounds)이 새 기본값으로 넘긴다. */
  text: { defaultWidth: 640, defaultHeight: 600, minWidth: 600, minHeight: 400 },
  /** world-quests, chat, admin-attendance, hr-management, scenarios.
   *  defaultWidth === minWidth 은 권위 commit e64acb5be — 1000 이상 floor 보장 의도. */
  standard: { defaultWidth: 1000, defaultHeight: 600, minWidth: 1000, minHeight: 600 },
  /** quests — 폭·하한은 standard. 기본 높이만 800: 600 이면 시나리오 상세가 548/770 이라
   *  제품 · 크리에이터 카드 아래가 잘렸다(2026-09-29 스토리북 `창 높이` 실측). */
  quests: { defaultWidth: 1000, defaultHeight: 800, minWidth: 1000, minHeight: 600 },
  /** attendance — 월 캘린더(5주) + 범례 + 하단 액션이 width 1000 기준 ≈696px.
   *  600 이면 마지막 주 아래(범례·휴가자 상세)가 폴드 밑으로 잘림. minHeight 는 standard floor 유지.
   *  width 는 standard floor 1000 대신 1100 carve-out — 정정 이력 서랍이 2열 그리드의 절반 폭이라
   *  1000 에서는 변경값 칸(`yyyy. MM. dd. HH:mm` ×2)이 눌려 잘렸다.
   *  standard 와 같은 의도로 defaultWidth === minWidth (default 가 곧 최소). */
  attendance: { defaultWidth: 1100, defaultHeight: 720, minWidth: 1100, minHeight: 600 },
  /** team-settings, finance, system-admin, bug-reports — wide admin 테이블.
   *  minWidth 는 advertiser 와 정합 (890→900 unified). */
  wideAdmin: { defaultWidth: 1000, defaultHeight: 600, minWidth: 900, minHeight: 500 },
  /** leaderboard — 짧은 height. */
  leaderboard: { defaultWidth: 800, defaultHeight: 550, minWidth: 800, minHeight: 400 },
  /** lounge — minW 800 으로 약간 유연. */
  lounge: { defaultWidth: 1000, defaultHeight: 600, minWidth: 800, minHeight: 500 },
  /** advertiser-console · creator-relations — 목록·상세 2열이 minWidth 900 에서 나란히 서는 하한.
   *  (이 수치를 푸터에 그대로 적어두던 self-doc 표기는 걷어냈다.) */
  advertiser: { defaultWidth: 1080, defaultHeight: 600, minWidth: 900, minHeight: 600 },
  /** wiki — 본문 · 오른쪽 카드 두 단(설계 axiom-wiki.md ③). 1000 아래에서는 카드가 본문 아래로,
   *  640 아래에서는 폰 모양으로 창 폭 컨테이너 쿼리가 가르므로 하한은 좁게 둔다. */
  wiki: { defaultWidth: 1200, defaultHeight: 760, minWidth: 640, minHeight: 500 },
  /** ceo-console — 임원 전용 와이드. */
  ceoConsole: { defaultWidth: 1100, defaultHeight: 700, minWidth: 1000, minHeight: 600 },
} as const;

/** 시나리오 창이 화면에서 차지할 세로 비율의 하한. */
const SCENARIO_MIN_VIEWPORT_RATIO = 0.7;

/**
 * 시나리오 창 — `standard` 에 세로만 얹은 carve-out (2026-09-22).
 *
 * 덮개 안에 첫 섭외메일 작성기(캔버스 600 + 조작 260)가 서는데, 하한 600 에서는 덮개 머리와
 * 작성기 머리줄을 뺀 세로가 450 안팎이라 캔버스 위쪽(보내는 사람·제목·이미지·본문)이 잘렸다
 * — dev 실측. 가로는 문제가 아니었다(작성기 880 은 `standard` 의 1000 안에 들어간다).
 *
 * **하한만 뷰포트로 잡는다.** 기본값에 뷰포트를 넣으면 프리셋 서명(`resolvePersistedBounds`)이
 * 뷰포트마다 달라져, 사용자가 맞춰 둔 크기가 창 크기 변화마다 1회씩 날아간다. 기본값은 상수로
 * 올려 기존 저장 크기를 **한 번만** 무효화한다.
 */
export function scenarioWindowSize(viewportHeight: number): {
  defaultWidth: number;
  defaultHeight: number;
  minWidth: number;
  minHeight: number;
} {
  return {
    ...liveWindowSize.standard,
    defaultHeight: 760,
    minHeight: Math.max(
      liveWindowSize.standard.minHeight,
      Math.round(viewportHeight * SCENARIO_MIN_VIEWPORT_RATIO)
    ),
  };
}
